import type { DealBookingType, ProductSelectorType } from '@src/types/OTC';
import { USD_PEGGED_NO_CONVERSION_INDEX, type FixingIndexMappingMap } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import {
  assertNever,
  isFloatingRateLoanProductTemplate,
  isOTCOptionProductTemplate,
  StrikeTypeEnum,
  toBig,
  type Security,
  type StrikeTypeEnum as StrikeTypeEnumType,
  type ToggleGroupOption,
} from '@talos/kyoko';
import type { ManualDealTouchState } from './types';

/**
 * Maximum number of decimal places allowed for Fixed strike prices
 */
export const MAX_STRIKE_PRICE_DECIMAL_PLACES = 4;

export function getManualDealProductTypeOptions(enableRepoProduct: boolean): ToggleGroupOption<ProductSelectorType>[] {
  const options: ToggleGroupOption<ProductSelectorType>[] = [
    { value: 'otc', displayName: 'OTC Derivative' },
    { value: 'loan', displayName: 'Loan' },
  ];
  if (enableRepoProduct) {
    options.push({ value: 'repo', displayName: 'Repo' });
  }
  return options;
}

export function mapBookingTypeToProductType(bookingType: DealBookingType): ProductSelectorType {
  switch (bookingType) {
    case 'forward':
    case 'option':
      return 'otc';
    case 'fixedRateLoan':
    case 'floatingRateLoan':
      return 'loan';
    case 'repo':
      return 'repo';
    default:
      return assertNever(bookingType);
  }
}

/**
 * Map header OTC vs Loan bucket to DealBookingType (uses template when loan or OTC to pick subtype).
 */
export function mapProductTypeToBookingType(
  productType: ProductSelectorType,
  templateSymbol?: Security
): DealBookingType {
  switch (productType) {
    case 'loan': {
      if (templateSymbol) {
        return isFloatingRateLoanProductTemplate(templateSymbol) ? 'floatingRateLoan' : 'fixedRateLoan';
      }
      return 'fixedRateLoan';
    }
    case 'otc': {
      if (templateSymbol) {
        return isOTCOptionProductTemplate(templateSymbol) ? 'option' : 'forward';
      }
      return 'forward';
    }
    case 'repo':
      return 'repo';
    default:
      return assertNever(productType);
  }
}

/**
 * Converts a decimal value (e.g., 0.15) to percentage format (e.g., 15).
 * @param decimalValue - The decimal value as a string (e.g., "0.15" for 15%)
 * @returns The percentage value as a string (e.g., "15"), or undefined if input is invalid
 */
export function convertDecimalToPercentage(decimalValue: string | undefined): string | undefined {
  if (!decimalValue) {
    return undefined;
  }
  const decimalBig = toBig(decimalValue);
  if (decimalBig == null) {
    return undefined;
  }
  return decimalBig.times(100).toFixed();
}

/**
 * Formats a "No Mark Found" message for display when a fixing mark is unavailable.
 * @param fixingName - Optional fixing name to include in the message
 * @returns Formatted message string: "(No Mark Found - {fixingName})" or "(No Mark Found)"
 */
export function formatNoMarkFoundMessage(fixingName?: string | null): string {
  return fixingName ? `(No Mark Found - ${fixingName})` : '(No Mark Found)';
}

/**
 * Get the error message for a field, only if the field has been touched
 * @param touchState - The touch state object
 * @param errors - The validation errors object
 * @param fieldName - The name of the field to check
 * @returns The error message if the field is touched and has an error, otherwise empty string
 */
export const getFieldError = (
  touchState: ManualDealTouchState,
  errors: { [key: string]: string } | undefined,
  fieldName: keyof ManualDealTouchState
): string => {
  return touchState[fieldName] && errors?.[fieldName] ? errors[fieldName] : '';
};

/**
 * Check if a field should be marked as invalid (touched and has error)
 * @param touchState - The touch state object
 * @param errors - The validation errors object
 * @param fieldName - The name of the field to check
 * @returns True if the field is touched and has an error, otherwise false
 */
export const isFieldInvalid = (
  touchState: ManualDealTouchState,
  errors: { [key: string]: string } | undefined,
  fieldName: keyof ManualDealTouchState
): boolean => {
  return !!(touchState[fieldName] && errors?.[fieldName]);
};

/**
 * True when commission is present and parses to a finite non-zero number.
 * Shared by Yup (commission currency required) and commission UI (e.g. single-currency auto-fill).
 */
export function hasNonZeroCommissionAmount(commission: string | number | null | undefined): boolean {
  if (commission == null || commission === '') {
    return false;
  }
  const n = Number(commission);
  return Number.isFinite(n) && n !== 0;
}

/**
 * Get the Unit Notional currency based on payoff type and underlying/quote asset
 * @param payoffType - The payoff type of the forward product
 * @param underlying - The underlying asset symbol
 * @param quoteAsset - The quote asset symbol
 * @returns The currency to display for the Unit Notional, or null if it can't be determined
 */
export const getNotionalCurrencies = (
  payoffType?: string | null,
  underlying?: string | null,
  quoteAsset?: string | null
): { unitNotionalCurrency: string | undefined; notionalCurrency: string | undefined } => {
  if (!payoffType || !underlying || !quoteAsset) {
    return { unitNotionalCurrency: undefined, notionalCurrency: undefined };
  }
  return {
    unitNotionalCurrency: payoffType === 'Linear' ? underlying : (quoteAsset ?? undefined),
    notionalCurrency: payoffType === 'Linear' ? quoteAsset : (underlying ?? undefined),
  };
};

/**
 * Build a map of PriceSource Name -> has Mark (for fast lookup)
 * @param fixingIndexMapping - The fixing index mapping map
 * @returns Map of price source names to whether they have marks
 */
export function buildPriceSourceHasMarkMap(fixingIndexMapping: FixingIndexMappingMap): Map<string, boolean> {
  const map = new Map<string, boolean>();
  fixingIndexMapping.forEach(mappings => {
    mappings.forEach(mapping => {
      // Only set to true if it has a Mark (Mark !== null)
      if (mapping.Mark !== null) {
        map.set(mapping.Name, true);
      }
    });
  });
  return map;
}

/**
 * Check if a price source has a valid mark (has mark or is USD_PEGGED_NO_CONVERSION_INDEX.Name for USD-pegged assets only)
 * @param priceSource - The price source name to check
 * @param asset - The asset symbol (e.g., 'USD', 'BTC', 'SOL')
 * @param priceSourceHasMarkMap - Map of price source names to whether they have marks
 * @returns True if the price source is valid (has mark or is USD_PEGGED_NO_CONVERSION_INDEX.Name for USD/USDT/USDC only)
 */
export function hasValidPriceSource(
  priceSource: string | undefined,
  asset: string | undefined,
  priceSourceHasMarkMap: Map<string, boolean>
): boolean {
  // If no PriceSource, exclude it
  if (!priceSource) {
    return false;
  }
  // Allow USD_PEGGED_NO_CONVERSION_INDEX.Name ONLY for USD-pegged assets
  // This is a special case where no price conversion is needed between USD/USDT/USDC
  if (priceSource === USD_PEGGED_NO_CONVERSION_INDEX.Name) {
    return asset === 'USD' || asset === 'USDT' || asset === 'USDC';
  }
  // Check if PriceSource has Marks
  return priceSourceHasMarkMap.get(priceSource) === true;
}

/**
 * Check if accepted collateral has at least one entry with a valid price source (has mark or is USD_PEGGED_NO_CONVERSION_INDEX.Name for USD-pegged assets only)
 * @param acceptedCollateral - Array of accepted collateral entries
 * @param priceSourceHasMarkMap - Map of price source names to whether they have marks
 * @returns True if at least one accepted collateral entry has a valid price source
 */
export function hasAcceptedCollateralWithValidPriceSource(
  acceptedCollateral: Array<{ Asset?: string; PriceSource?: string }> | undefined,
  priceSourceHasMarkMap: Map<string, boolean>
): boolean {
  if (!Array.isArray(acceptedCollateral) || acceptedCollateral.length === 0) {
    return false;
  }
  // Check if at least one accepted collateral entry has a valid price source
  return acceptedCollateral.some(ac => hasValidPriceSource(ac.PriceSource, ac.Asset, priceSourceHasMarkMap));
}

/**
 * Formats strike price for display based on strike type
 * - Delta: rounds to integer and adds "D" suffix (e.g., "10D", "25D")
 * - Fixed: formats as decimal with max 4 decimal places
 * @param value - The strike price value as a string
 * @param strikeType - The strike type (Fixed or Delta)
 * @returns Formatted strike price string
 */
export function formatStrikePriceDisplay(value: string | undefined | null, strikeType: StrikeTypeEnumType): string {
  if (!value) {
    return '';
  }

  if (strikeType === StrikeTypeEnum.Delta) {
    const num = Number(value);
    if (isNaN(num)) {
      return value;
    }
    return `${Math.round(num)}D`;
  } else {
    // Fixed strike - format with max decimal places using Big.js for precision
    const big = toBig(value);
    if (big == null) {
      return value;
    }
    // Round to MAX_STRIKE_PRICE_DECIMAL_PLACES using Big.js for precision
    const rounded = Number(big.toFixed(MAX_STRIKE_PRICE_DECIMAL_PLACES));
    return rounded.toString();
  }
}
